// Centralised promo-rotation calendar for NIVARA.
//
// One promo is "active" per calendar month. The PromoBanner reads this list
// and picks the entry whose `month` matches `new Date()` (year-month string).
// Adding a new entry = adding one object. Removing = deleting one row.
//
// Each promo MUST also be honoured server-side in backend/server.py
// (Literal type on ContractCreate.promo + discount logic in compute_price).

/**
 * @typedef {Object} Promo
 * @property {string}  code      Backend promo code (must match server Literal)
 * @property {string}  month     "YYYY-MM" — calendar month when this promo is active
 * @property {string}  label     Headline shown on the banner ribbon
 * @property {string}  vehicle   "Mercedes Vito" | "Renault Master"
 * @property {string}  title     Big multiline title (use \n for line breaks)
 * @property {number}  priceChf  Sale price displayed in big gold font
 * @property {number=} fromChf   Optional "statt CHF X.–" struck-through price
 * @property {Array<{icon: string, text: string}>} bullets Up to 3 feature bullets (icon names from PROMO_ICONS)
 * @property {string}  deepLink  Path with ?promo=… that pre-selects the offer in /
 */

export const PROMO_CALENDAR = [
  {
    code: "vito_3d",
    month: "2026-05",
    label: "Vito · 3 Tage Aktion",
    vehicle: "Mercedes Vito",
    title: "3 Tage\nab CHF",
    priceChf: 500,
    fromChf: null,
    bullets: [
      { icon: "calendar", text: "3 Tage · 24h × 3" },
      { icon: "infinity", text: "Ohne km-Limit" },
      { icon: "flag", text: "Nur in der Schweiz" },
    ],
    deepLink: "/?promo=vito_3d&days=3",
  },
  {
    code: "vito_3d",
    month: "2026-06",
    label: "Vito · 3 Tage Aktion",
    vehicle: "Mercedes Vito",
    title: "3 Tage\nab CHF",
    priceChf: 500,
    fromChf: null,
    bullets: [
      { icon: "calendar", text: "3 Tage · 24h × 3" },
      { icon: "infinity", text: "Ohne km-Limit" },
      { icon: "flag", text: "Nur in der Schweiz" },
    ],
    deepLink: "/?promo=vito_3d&days=3",
  },
  {
    code: "master_6h",
    month: "2026-07",
    label: "Master · 6 Stunden",
    vehicle: "Renault Master",
    title: "6 Stunden\nnur CHF",
    priceChf: 100,
    fromChf: 140,
    bullets: [
      { icon: "clock", text: "6 Stunden Paket" },
      { icon: "package", text: "200 km inkl." },
      { icon: "truck", text: "Bis 1500 kg Nutzlast" },
    ],
    deepLink: "/?promo=master_6h",
  },
  {
    code: "vito_7d_eu",
    month: "2026-08",
    label: "Vito · 7 Tage Europa",
    vehicle: "Mercedes Vito",
    title: "7 Tage\nEuropa CHF",
    priceChf: 1400,
    fromChf: 1610,
    bullets: [
      { icon: "calendar", text: "7 Tage komplett" },
      { icon: "infinity", text: "Ohne km-Limit" },
      { icon: "globe", text: "Angrenzende EU-Länder" },
    ],
    deepLink: "/?promo=vito_7d_eu&days=7",
  },
  // Rotation continues — same 3 promos cycle until end of year
  {
    code: "vito_3d",
    month: "2026-09",
    label: "Vito · 3 Tage Aktion",
    vehicle: "Mercedes Vito",
    title: "3 Tage\nab CHF",
    priceChf: 500,
    fromChf: null,
    bullets: [
      { icon: "calendar", text: "3 Tage · 24h × 3" },
      { icon: "infinity", text: "Ohne km-Limit" },
      { icon: "flag", text: "Nur in der Schweiz" },
    ],
    deepLink: "/?promo=vito_3d&days=3",
  },
  {
    code: "master_6h",
    month: "2026-10",
    label: "Master · 6 Stunden",
    vehicle: "Renault Master",
    title: "6 Stunden\nnur CHF",
    priceChf: 100,
    fromChf: 140,
    bullets: [
      { icon: "clock", text: "6 Stunden Paket" },
      { icon: "package", text: "200 km inkl." },
      { icon: "truck", text: "Bis 1500 kg Nutzlast" },
    ],
    deepLink: "/?promo=master_6h",
  },
  {
    code: "vito_7d_eu",
    month: "2026-11",
    label: "Vito · 7 Tage Europa",
    vehicle: "Mercedes Vito",
    title: "7 Tage\nEuropa CHF",
    priceChf: 1400,
    fromChf: 1610,
    bullets: [
      { icon: "calendar", text: "7 Tage komplett" },
      { icon: "infinity", text: "Ohne km-Limit" },
      { icon: "globe", text: "Angrenzende EU-Länder" },
    ],
    deepLink: "/?promo=vito_7d_eu&days=7",
  },
  {
    code: "vito_3d",
    month: "2026-12",
    label: "Winter-Aktion Vito · 3 Tage",
    vehicle: "Mercedes Vito",
    title: "3 Tage\nab CHF",
    priceChf: 500,
    fromChf: null,
    bullets: [
      { icon: "calendar", text: "3 Tage · 24h × 3" },
      { icon: "infinity", text: "Ohne km-Limit" },
      { icon: "flag", text: "Nur in der Schweiz" },
    ],
    deepLink: "/?promo=vito_3d&days=3",
  },
];

/**
 * Return the promo active for the supplied date (defaults to now). Returns
 * null when there's no entry — banner then auto-hides.
 */
export const getActivePromo = (now = new Date()) => {
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  return PROMO_CALENDAR.find((p) => p.month === ym) || null;
};

/**
 * End of the current promo's calendar month — used as the countdown target.
 */
export const getMonthEnd = (now = new Date()) => {
  // Last day of current month at 23:59:59 local Swiss time
  return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
};
